import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, TrendingUp, Shield, Target, Calendar, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropFirm() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/prop-firm/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts');
    }
  };

  const handlePurchase = async (accountSize) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to purchase an account');
      navigate('/pricing');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/prop-firm/purchase`,
        { account_size: accountSize },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Account purchased! Number: ${response.data.account_number}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#00E676]/10 border border-[#00E676]/30 rounded-full px-6 py-2 mb-6">
              <Shield className="w-4 h-4 text-[#00E676]" />
              <span className="text-sm font-medium text-[#00E676]">Funded Trading Accounts</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Get Funded & Trade with <span className="text-[#00E676]">Our Capital</span>
            </h1>
            <p className="text-lg text-[#A1A1AA] max-w-3xl mx-auto">
              Pass the evaluation, keep up to 80% of profits, and trade with no personal risk. 
              Professional prop firm accounts from $5K to $100K.
            </p>
          </div>

          {/* Account Packages */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {accounts.map((account) => (
              <Card
                key={account.id}
                data-testid={`prop-account-${account.account_size}`}
                className={`bg-[#111111] border rounded-lg relative ${
                  account.account_size === '25k' 
                    ? 'border-[#00E676] shadow-lg shadow-[#00E676]/20' 
                    : 'border-[#27272A]'
                }`}
              >
                {account.account_size === '25k' && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#00E676] text-black px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}

                <CardHeader>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-[#00E676]" />
                    </div>
                    <CardTitle className="text-3xl font-bold mb-2">
                      ${account.account_size.toUpperCase()}
                    </CardTitle>
                    <p className="text-[#A1A1AA]">Account Size</p>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="text-center">
                    <p className="text-2xl sm:text-4xl font-bold font-data text-[#00E676]">
                      ${account.price}
                    </p>
                    <p className="text-sm text-[#A1A1AA]">One-time fee</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-[#00E676]" />
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Profit Target</p>
                        <p className="font-bold font-data">${account.profit_target.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[#FFC107]" />
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Max Drawdown</p>
                        <p className="font-bold font-data">${account.max_drawdown.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#007AFF]" />
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Trading Period</p>
                        <p className="font-bold font-data">{account.trading_period} days</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-[#27272A] pt-4">
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#00E676]" />
                        <span>Keep 80% of profits</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#00E676]" />
                        <span>Trade major forex pairs</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#00E676]" />
                        <span>No time restrictions</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#00E676]" />
                        <span>Weekly payouts</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#00E676]" />
                        <span>Unlimited retakes</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    data-testid={`buy-prop-${account.account_size}-btn`}
                    onClick={() => handlePurchase(account.account_size)}
                    disabled={loading}
                    className={`w-full ${
                      account.account_size === '25k'
                        ? 'bg-[#00E676] hover:bg-[#00C853] text-black'
                        : 'bg-[#1C1C1E] hover:bg-[#27272A] text-white'
                    } rounded-md`}
                  >
                    {loading ? 'Processing...' : 'Get Funded'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Evaluation Rules */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-center">Evaluation Rules</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#00E676]" />
                  What You Can Do
                </h3>
                <ul className="space-y-2 text-[#A1A1AA]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E676]">✓</span>
                    <span>Trade all major and minor forex pairs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E676]">✓</span>
                    <span>Use any trading strategy (scalping, swing, day trading)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E676]">✓</span>
                    <span>Trade during news events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E676]">✓</span>
                    <span>Hold trades overnight and over weekends</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E676]">✓</span>
                    <span>Use Expert Advisors (EAs) and trading bots</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#FF3D00]" />
                  Rules to Follow
                </h3>
                <ul className="space-y-2 text-[#A1A1AA]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF3D00]">×</span>
                    <span>Don't exceed maximum daily loss limit (5% of account)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF3D00]">×</span>
                    <span>Don't breach overall max drawdown limit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF3D00]">×</span>
                    <span>Minimum 5 trading days required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF3D00]">×</span>
                    <span>No copy trading or signal services allowed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF3D00]">×</span>
                    <span>No hedging or arbitrage strategies</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
